import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { flushSync } from 'react-dom';

import {
  TASTATUR_AB_BREITE,
  TASTATUR_REIHEN,
  feldLeeren,
  zeichenEinfuegen,
  zeichenFuerTaste,
  zeichenLoeschen,
  type Eingabe,
} from '../bildschirmtastatur';

/**
 * Eingebaute Bildschirmtastatur (BT v3, freigegeben 2026-11-02; Rahmen in Section `382:14947`,
 * Komponenten `370:11079`). Die Tasten nehmen nie den Fokus (pointerdown/mousedown ohne
 * Standardwirkung) und schreiben nur in ein registriertes Zielfeld (R1), nie über
 * `document.activeElement`. Hardware-Tastatur und Scanner laufen unverändert über das Feld.
 */

/** Esc-Ereignisse, mit denen die Tastatur geschlossen wurde (R5: erstes Esc nur für sie). */
const verbrauchteEsc = new WeakSet<Event>();

/**
 * R5: Hat dieses Esc nur die Bildschirmtastatur geschlossen? Dann wirkt es sonst nirgends (Suche
 * leeren, Dialog schließen, globales Esc). Nimmt DOM- und React-Ereignisse.
 */
export function istTastaturEsc(e: Event | SyntheticEvent): boolean {
  return verbrauchteEsc.has('nativeEvent' in e ? e.nativeEvent : e);
}

/** Fachliche Angaben eines Zielfelds. */
export interface TastaturFeld {
  /** Feldname für „Eingabe in: …“. */
  name: string;
  /** Wert setzen wie `onChange` des Felds. */
  setzen: (wert: string) => void;
  /** R2: Bestätigen-Taste mit fachlichem Handler; ohne = „Fertig“ (schließt nur die Tastatur). */
  bestaetigen?: { text: string; aktion: () => void };
  /** BT-E3: Umschaltung „Groß“ beim Öffnen (Gutscheincode). */
  gross?: boolean;
  /** Nach „Fertig“ bzw. Schließen: eigener Fokus statt des Tastatur-Knopfs (R3: Betrag). */
  nachSchliessen?: () => void;
  /** Bisheriges `onFocus` des Felds (wird zusätzlich aufgerufen). */
  beimFokus?: () => void;
}

interface Eintrag {
  feld: TastaturFeld;
  el: HTMLInputElement | null;
}

function nutzbar(el: HTMLInputElement | null | undefined): el is HTMLInputElement {
  return el !== null && el !== undefined && el.isConnected && !el.disabled && !el.readOnly;
}

/** BT-E4: ab 601 px Breite (darunter Smartphone-Ansicht mit System-Tastatur). */
function useBreitGenug(): boolean {
  const abfrage = `(min-width: ${String(TASTATUR_AB_BREITE)}px)`;
  const [breit, setBreit] = useState(() =>
    typeof window.matchMedia === 'function' ? window.matchMedia(abfrage).matches : true,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const m = window.matchMedia(abfrage);
    const beiWechsel = (): void => {
      setBreit(m.matches);
    };
    beiWechsel();
    m.addEventListener('change', beiWechsel);
    return () => {
      m.removeEventListener('change', beiWechsel);
    };
  }, [abfrage]);
  return breit;
}

/**
 * Steuerung einer Bildschirmtastatur (je Verkaufsfläche bzw. Dialog eine). Startet geschlossen
 * und ohne Ziel (BT-E2); ein neuer Dialog bekommt eine neue Instanz.
 */
export function useBildschirmtastatur(optionen: { standardZiel?: string } = {}) {
  const id = useId();
  const tastaturId = `bt${id.replace(/:/g, '')}`;
  const breitGenug = useBreitGenug();
  const [offen, setOffen] = useState(false);
  const [zielSchluessel, setZielSchluessel] = useState<string | null>(null);
  const [gross, setGross] = useState(false);
  const felder = useRef(new Map<string, Eintrag>());
  const knoepfe = useRef(new Map<string, HTMLButtonElement | null>());
  const feldRefs = useRef(new Map<string, (el: HTMLInputElement | null) => void>());
  const knopfRefs = useRef(new Map<string, (el: HTMLButtonElement | null) => void>());
  const standard = optionen.standardZiel;

  /** R1: aktuelles Ziel – zuletzt aktives nutzbares Feld, sonst das Standardfeld. */
  const zielJetzt = (): {
    schluessel: string;
    eintrag: Eintrag & { el: HTMLInputElement };
  } | null => {
    for (const s of [zielSchluessel, standard]) {
      if (s === null || s === undefined) {
        continue;
      }
      const e = felder.current.get(s);
      if (e !== undefined && nutzbar(e.el)) {
        return { schluessel: s, eintrag: e as Eintrag & { el: HTMLInputElement } };
      }
    }
    return null;
  };
  const ziel = zielJetzt();

  const schliessen = useCallback(
    (fokus: 'knopf' | 'feld' | 'keiner' = 'knopf'): void => {
      setOffen(false);
      const z = zielSchluessel ?? standard ?? null;
      const e = z === null ? undefined : felder.current.get(z);
      const el = e?.el;
      if (fokus === 'keiner') {
        // Dialog-, Bediener- oder Feldwechsel: keine alte Zuordnung übernehmen (BT-E2)
        setZielSchluessel(null);
        return;
      }
      if (fokus === 'feld') {
        if (nutzbar(el)) {
          el.removeAttribute('inputmode');
          el.focus();
        }
        return;
      }
      if (e?.feld.nachSchliessen !== undefined) {
        e.feld.nachSchliessen();
        return;
      }
      // Fokus nur verschieben, wenn er im Feld lag – sonst öffnete das Zurücksetzen von
      // inputMode an Android/iPad sofort die System-Tastatur.
      // Auch Fokus auf einer Taste (per Tab erreicht): die Taste verschwindet gleich
      const aufTaste =
        document.getElementById(tastaturId)?.contains(document.activeElement) === true;
      if (el !== null && el !== undefined && (document.activeElement === el || aufTaste)) {
        const k = (z === null ? undefined : knoepfe.current.get(z)) ?? knoepfe.current.get('');
        if (k?.isConnected === true) {
          k.focus();
        } else {
          el.blur();
        }
      }
    },
    [zielSchluessel, standard, tastaturId],
  );
  const schliessenRef = useRef(schliessen);
  schliessenRef.current = schliessen;

  // R5: erstes Esc schließt nur die Tastatur. Erfassungsphase am Fenster – vor den Feldern,
  // Dialogen und dem globalen Esc des Verkaufs, die das Ereignis über `istTastaturEsc` erkennen.
  // Keine Weitergabesperre: Leerlauf-Wächter und Scanner sehen die Taste weiter.
  useEffect(() => {
    if (!offen) {
      return;
    }
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !e.isComposing && !verbrauchteEsc.has(e)) {
        e.preventDefault();
        verbrauchteEsc.add(e);
        schliessenRef.current();
      }
    };
    window.addEventListener('keydown', beiTaste, true);
    return () => {
      window.removeEventListener('keydown', beiTaste, true);
    };
  }, [offen]);

  // Ziel entfernt oder deaktiviert (R1) bzw. Fenster zu schmal (BT-E4): Tastatur schließen.
  // Nach dem Commit neu prüfen – Refs entfernter Felder sind erst dann leer.
  const zielJetztRef = useRef(zielJetzt);
  zielJetztRef.current = zielJetzt;
  useEffect(() => {
    if (offen && (zielJetztRef.current() === null || !breitGenug)) {
      schliessenRef.current('keiner');
    }
  });

  // Zielfeld nach dem Layoutwechsel sichtbar machen (Dialoginhalt schrumpft erst mit der Tastatur)
  const zielElJetzt = offen ? (ziel?.eintrag.el ?? null) : null;
  useLayoutEffect(() => {
    if (zielElJetzt === null) {
      return;
    }
    zielElJetzt.scrollIntoView({ block: 'nearest' });
    // Dialoginhalt ändert sich (Treffer davor, Fenster kleiner): Ziel erneut sichtbar machen
    const bereich = zielElJetzt.closest<HTMLElement>('.dlg-inhalt');
    const sichtbar = (): void => {
      if (zielElJetzt.isConnected) {
        zielElJetzt.scrollIntoView({ block: 'nearest' });
      }
    };
    const groesse = typeof ResizeObserver === 'function' ? new ResizeObserver(sichtbar) : null;
    const inhalt = typeof MutationObserver === 'function' ? new MutationObserver(sichtbar) : null;
    if (bereich !== null) {
      groesse?.observe(bereich);
      inhalt?.observe(bereich, { childList: true, subtree: true });
    }
    window.addEventListener('resize', sichtbar);
    return () => {
      groesse?.disconnect();
      inhalt?.disconnect();
      window.removeEventListener('resize', sichtbar);
    };
  }, [zielElJetzt]);

  function oeffnen(schluessel?: string): void {
    const s = schluessel ?? zielSchluessel ?? standard;
    if (s === undefined) {
      return;
    }
    const e = felder.current.get(s);
    if (e === undefined || !nutzbar(e.el) || !breitGenug) {
      return;
    }
    // R1: inputMode vor dem Fokussieren setzen – sonst öffnete die System-Tastatur zusätzlich
    e.el.inputMode = 'none';
    setZielSchluessel(s);
    setGross(e.feld.gross === true);
    setOffen(true);
    e.el.focus();
    e.el.scrollIntoView({ block: 'nearest' });
  }

  function anwenden(f: (e: Eingabe) => Eingabe): void {
    const z = zielJetzt();
    if (z === null) {
      return;
    }
    const el = z.eintrag.el;
    const n = el.value.length;
    const vorher = { wert: el.value, start: el.selectionStart ?? n, ende: el.selectionEnd ?? n };
    const nachher = f(vorher);
    if (nachher.wert !== vorher.wert) {
      // Gesteuertes Feld: neuer Wert synchron im DOM, damit der Cursor danach stimmt
      flushSync(() => {
        z.eintrag.feld.setzen(nachher.wert);
      });
    }
    if (document.activeElement !== el) {
      el.focus({ preventScroll: true });
    }
    try {
      el.setSelectionRange(nachher.start, nachher.ende);
    } catch {
      // Feldarten ohne Auswahl – Wert ist gesetzt
    }
  }

  /** Eigenschaften für ein Zielfeld (`<input {...bt.feld('suche', {...})} />`). */
  function feld(schluessel: string, art: TastaturFeld) {
    const alt = felder.current.get(schluessel);
    felder.current.set(schluessel, { feld: art, el: alt?.el ?? null });
    let ref = feldRefs.current.get(schluessel);
    if (ref === undefined) {
      ref = (el: HTMLInputElement | null) => {
        const e = felder.current.get(schluessel);
        if (e !== undefined) {
          e.el = el;
        }
      };
      feldRefs.current.set(schluessel, ref);
    }
    return {
      ref,
      onFocus: (ev: { currentTarget: HTMLInputElement }) => {
        art.beimFokus?.();
        if (zielSchluessel !== schluessel) {
          setZielSchluessel(schluessel);
          if (offen) {
            setGross(art.gross === true);
          }
        }
        if (offen) {
          ev.currentTarget.scrollIntoView({ block: 'nearest' });
        }
      },
      ...(offen ? { inputMode: 'none' as const } : {}),
    };
  }

  /**
   * Eigenschaften für den Knopf „Tastatur“. Mit Schlüssel: Knopf neben diesem Feld; ohne:
   * Knopf im Dialogkopf für das zuletzt aktive Feld bzw. das Standardziel (R1).
   */
  function knopf(schluessel?: string) {
    const s = schluessel ?? '';
    let ref = knopfRefs.current.get(s);
    if (ref === undefined) {
      ref = (el: HTMLButtonElement | null) => {
        knoepfe.current.set(s, el);
      };
      knopfRefs.current.set(s, ref);
    }
    const anDiesemFeld = offen && (schluessel === undefined || ziel?.schluessel === schluessel);
    return {
      ref,
      offen: anDiesemFeld,
      tastaturId,
      verfuegbar: breitGenug,
      onUmschalten: () => {
        if (anDiesemFeld) {
          schliessen('feld');
        } else {
          oeffnen(schluessel);
        }
      },
    };
  }

  const zielFeld = ziel?.eintrag.feld ?? null;
  const tastatur =
    offen && zielFeld !== null
      ? {
          id: tastaturId,
          // Getter: die Tastatur rendert nach dem Feld und liest so dessen aktuelle Angaben
          get zielName(): string {
            return (zielJetzt()?.eintrag.feld ?? zielFeld).name;
          },
          get bestaetigenText(): string {
            return (zielJetzt()?.eintrag.feld ?? zielFeld).bestaetigen?.text ?? 'Fertig';
          },
          gross,
          onZeichen: (t: string) => {
            anwenden((e) =>
              zeichenEinfuegen(e, zeichenFuerTaste(t, gross), ziel?.eintrag.el.maxLength),
            );
          },
          onLeerzeichen: () => {
            anwenden((e) => zeichenEinfuegen(e, ' ', ziel?.eintrag.el.maxLength));
          },
          onRueck: () => {
            anwenden(zeichenLoeschen);
          },
          onLeeren: () => {
            anwenden(() => feldLeeren());
          },
          onGross: () => {
            setGross((g) => !g);
          },
          onBestaetigen: () => {
            // Handler aus dem letzten Rendern des Felds (aktueller Wert), nicht aus diesem
            const b = (zielJetzt()?.eintrag.feld ?? zielFeld).bestaetigen;
            if (b === undefined) {
              schliessen();
            } else {
              b.aktion();
            }
          },
          onSchliessen: () => {
            schliessen();
          },
        }
      : null;

  return {
    offen: tastatur !== null,
    /** Schlüssel des aktuellen Ziels (nur bei offener Tastatur). */
    zielSchluessel: tastatur === null ? null : (ziel?.schluessel ?? null),
    /** Element des aktuellen Ziels (nur bei offener Tastatur; für die Lage). */
    zielElement: tastatur === null ? null : (ziel?.eintrag.el ?? null),
    feld,
    knopf,
    oeffnen,
    schliessen,
    tastatur,
  };
}

/** Die Tasten nehmen nie den Fokus – das Zielfeld behält Cursor und Auswahl. */
const fokusHalten = {
  onPointerDown: (e: { preventDefault: () => void }) => {
    e.preventDefault();
  },
  onMouseDown: (e: { preventDefault: () => void }) => {
    e.preventDefault();
  },
};

function TastaturSymbol() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d="M3 6h18v12H3zM6 9h1M9 9h1M12 9h1M15 9h1M18 9h0M6 12h1M9 12h1M12 12h1M15 12h1M18 12h0M8 15h8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Knopf „Tastatur“ (BT/Tastatur-Knopf: M 56 px neben der Suche, S 48 px in Dialogen). */
export function TastaturKnopf(props: {
  steuerung: ReturnType<ReturnType<typeof useBildschirmtastatur>['knopf']>;
  klein?: boolean;
  /** Zugänglicher Name, falls mehrere Knöpfe auf einer Fläche stehen. */
  beschriftung?: string;
}) {
  const s = props.steuerung;
  if (!s.verfuegbar) {
    return null;
  }
  return (
    <button
      ref={s.ref}
      type="button"
      className={`taste zweit bt-knopf${props.klein === true ? ' bt-knopf-klein' : ''}${s.offen ? ' gedrueckt' : ''}`}
      aria-expanded={s.offen}
      aria-controls={s.tastaturId}
      {...(props.beschriftung === undefined ? {} : { 'aria-label': props.beschriftung })}
      onClick={s.onUmschalten}
    >
      <TastaturSymbol />
      Tastatur
    </button>
  );
}

/** Die Tastatur selbst (BT/Bildschirmtastatur). `warnzeile` steht über den Tasten (R6). */
export function Bildschirmtastatur(props: {
  steuerung: NonNullable<ReturnType<typeof useBildschirmtastatur>['tastatur']>;
  warnzeile?: ReactNode;
  klasse?: string;
}) {
  const t = props.steuerung;
  const kopfId = `${t.id}-ziel`;
  return (
    <div
      id={t.id}
      className={
        props.klasse === undefined ? 'bildschirmtastatur' : `bildschirmtastatur ${props.klasse}`
      }
      role="group"
      aria-label="Bildschirmtastatur"
      aria-describedby={kopfId}
    >
      {props.warnzeile}
      <div className="bt-kopf">
        <span id={kopfId} className="bt-ziel">
          Eingabe in: <strong>{t.zielName}</strong>
        </span>
        <span className="bt-hinweis" title="Hardware-Tastatur und Scanner bleiben aktiv">
          Hardware-Tastatur und Scanner bleiben aktiv
        </span>
        <button
          type="button"
          className="taste zweit bt-schliessen"
          {...fokusHalten}
          onClick={t.onSchliessen}
        >
          Schließen ✕
        </button>
      </div>
      {TASTATUR_REIHEN.map((reihe, i) => (
        <div key={i} className="bt-reihe">
          {reihe.map((taste) => {
            const z = zeichenFuerTaste(taste, t.gross);
            return (
              <button
                key={taste}
                type="button"
                className="taste bt-taste"
                {...fokusHalten}
                onClick={() => {
                  t.onZeichen(taste);
                }}
              >
                {z}
              </button>
            );
          })}
        </div>
      ))}
      <div className="bt-reihe bt-funktionen">
        <button
          type="button"
          className={`taste zweit bt-taste bt-gross${t.gross ? ' gedrueckt' : ''}`}
          aria-pressed={t.gross}
          {...fokusHalten}
          onClick={t.onGross}
        >
          ⇧ Groß
        </button>
        <button
          type="button"
          className="taste zweit bt-taste bt-leer"
          {...fokusHalten}
          onClick={t.onLeerzeichen}
        >
          Leerzeichen
        </button>
        <button
          type="button"
          className="taste zweit bt-taste"
          aria-label="Zeichen löschen"
          {...fokusHalten}
          onClick={t.onRueck}
        >
          ← Zeichen
        </button>
        <button
          type="button"
          className="taste zweit bt-taste"
          {...fokusHalten}
          onClick={t.onLeeren}
        >
          Leeren
        </button>
        <button
          type="button"
          className="taste bt-taste bt-bestaetigen"
          {...fokusHalten}
          onClick={t.onBestaetigen}
        >
          {t.bestaetigenText}
        </button>
      </div>
    </div>
  );
}

/**
 * R6 Warnzeile bei offener Tastatur: alle Warnungen in einer Zeile von 44 px – erste als Titel
 * (gekürzt), dahinter „+N · alle anzeigen“. Der volle Text bleibt für Hilfstechnik lesbar; neue
 * Warnungen sagt eine Live-Region einmal an (nicht bei jedem Tastendruck).
 */
export function TastaturWarnzeile(props: {
  warnungen: { titel: string; weitere: number; alle: string[] } | null;
  onAlleAnzeigen: () => void;
}) {
  const w = props.warnungen;
  const text = w === null ? '' : w.alle.join(' · ');
  const [angesagt, setAngesagt] = useState('');
  useEffect(() => {
    setAngesagt(text);
  }, [text]);
  return (
    <>
      <span className="unsichtbar" aria-live="polite">
        {angesagt === '' ? '' : `Warnung: ${angesagt}`}
      </span>
      {w !== null && (
        <div className="bt-warnzeile">
          <span className="bt-warn-titel" aria-hidden="true">
            {w.titel}
          </span>
          <span className="unsichtbar">{text}</span>
          <button
            type="button"
            className="bt-warn-alle"
            {...fokusHalten}
            onClick={props.onAlleAnzeigen}
          >
            {w.weitere > 0 ? `+${String(w.weitere)} · alle anzeigen` : 'alle anzeigen'}
          </button>
        </div>
      )}
    </>
  );
}

/** Abstand der Tastatur zu Summe, Zahlleiste und bearbeitetem Feld. */
const LUFT = 8;
/** Artikelbereich ab dieser Breite: Tastatur nur über den Kacheln (Desktop), sonst über Bon und Kacheln. */
const ARTIKEL_BREIT_GENUG = 620;

/**
 * R6 Lage auf der Verkaufsfläche. Ab 901 px (Raster mit drei Spalten): unter der Suche, endet
 * über Summe und Zahlleiste und – liegt sie über dem Bon – über dem bearbeiteten Feld (Belegnotiz);
 * Desktop nur über den Kacheln, Tablet quer über Bonliste und Kacheln (BT-E1). Darunter
 * (Tablet hoch, gestapelt): volle Breite über der klebenden Zahlleiste, das Feld wird darüber
 * gescrollt. Verdeckte Bereiche sind `inert` (nicht per Tab erreichbar).
 */
export function VerkaufsTastatur(props: {
  steuerung: NonNullable<ReturnType<typeof useBildschirmtastatur>['tastatur']>;
  wurzel: { readonly current: HTMLElement | null };
  zielElement: HTMLElement | null;
  warnzeile?: ReactNode;
}) {
  const { wurzel, zielElement } = props;
  const [lage, setLage] = useState<{
    stil: CSSProperties;
    verdeckt: string[];
    /** Gestapelt über der Zahlleiste (Belegnotiz): Seite verlängern, Feld darüber halten. */
    stapelUnten?: boolean;
  } | null>(null);
  const [eigenHoehe, setEigenHoehe] = useState(0);
  /** Fensterhöhe: Bei kleinerem Fenster Feld und Summe erneut über die Tastatur holen. */
  const [fensterHoehe, setFensterHoehe] = useState(0);
  const eigen = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const berechnen = (): void => {
      const w = wurzel.current;
      if (w === null) {
        return;
      }
      const rechteck = (s: string): DOMRect | null =>
        w.querySelector<HTMLElement>(s)?.getBoundingClientRect() ?? null;
      const vh = window.innerHeight;
      setFensterHoehe(vh);
      const vw = window.innerWidth;
      const zahl = rechteck('.zahl-leiste');
      if (vw <= 900) {
        const unten = zahl === null ? vh - 16 : zahl.top - LUFT;
        const sucheG = rechteck('.suche-leiste');
        // BT3-TH1: Artikelsuche – volle Breite direkt unter der Suche; andere Felder (Belegnotiz)
        // über der klebenden Zahlleiste, das Feld wird darüber gescrollt
        if (sucheG !== null && zielElement?.closest('.suche-leiste') != null) {
          const oben = Math.max(sucheG.bottom + LUFT, LUFT);
          setLage({
            stil: { left: 16, right: 16, top: oben, maxHeight: Math.max(unten - oben, 0) },
            // Summe steht gestapelt in der klebenden Zahlleiste und bleibt frei
            verdeckt: ['.gruppen', '.kachel-feld', '.bon-panel'],
          });
          return;
        }
        setLage({
          stil: { left: 16, right: 16, bottom: vh - unten, maxHeight: Math.max(unten - LUFT, 0) },
          // Alles außer Notiz (Bereich mit dem Ziel), Summe und Zahlleiste – auch nach eigenem
          // Zurückscrollen nicht per Tab hinter der Tastatur erreichbar. Bereiche, die das Ziel
          // enthalten, bleiben frei (siehe Sperr-Effekt); so auch Rabatt/Bediener im Bon gesperrt.
          verdeckt: [
            '.funktionen',
            '.suche-leiste',
            '.gruppen',
            '.artikel-inhalt',
            '.bon-panel > *:not(.bon-summen)',
            '.bon-werkzeuge > *',
          ],
          stapelUnten: true,
        });
        return;
      }
      const suche = rechteck('.suche-leiste');
      const artikel = rechteck('.artikel-bereich');
      const bon = rechteck('.bon-panel');
      const summen = rechteck('.bon-summen');
      if (suche === null || artikel === null || bon === null) {
        return;
      }
      const nurArtikel = artikel.width >= ARTIKEL_BREIT_GENUG;
      const links = nurArtikel ? artikel.left : bon.left;
      let unten = zahl?.top ?? vh;
      if (!nurArtikel) {
        unten = Math.min(unten, summen?.top ?? unten);
        const f = zielElement?.getBoundingClientRect();
        if (f !== undefined && f.left < bon.right && f.top > suche.bottom) {
          unten = Math.min(unten, f.top);
        }
      }
      const oben = suche.bottom + LUFT;
      unten -= LUFT;
      setLage({
        stil: {
          left: links,
          width: artikel.right - links,
          bottom: vh - unten,
          maxHeight: Math.max(unten - oben, 0),
        },
        verdeckt: nurArtikel
          ? ['.gruppen', '.kachel-feld']
          : ['.gruppen', '.kachel-feld', '.bon-inhalt'],
      });
    };
    berechnen();
    const beobachter = typeof ResizeObserver === 'function' ? new ResizeObserver(berechnen) : null;
    const w = wurzel.current;
    if (w !== null) {
      beobachter?.observe(w);
    }
    window.addEventListener('resize', berechnen);
    // Gestapelte Ansicht scrollt die Seite – Lage folgt der Suche
    window.addEventListener('scroll', berechnen, { passive: true });
    return () => {
      beobachter?.disconnect();
      window.removeEventListener('resize', berechnen);
      window.removeEventListener('scroll', berechnen);
    };
  }, [wurzel, zielElement]);

  // Verdeckte Bereiche nicht per Tab erreichbar (R6)
  const verdecktSchluessel = lage?.verdeckt.join('|') ?? '';
  useEffect(() => {
    const w = wurzel.current;
    if (w === null || verdecktSchluessel === '') {
      return;
    }
    // Nur selbst gesperrte Elemente wieder freigeben; Bereiche mit dem Ziel nie sperren
    const els = verdecktSchluessel
      .split('|')
      .flatMap((s) => [...w.querySelectorAll<HTMLElement>(s)])
      .filter((el) => !el.inert && (zielElement === null || !el.contains(zielElement)));
    for (const el of els) {
      el.inert = true;
    }
    return () => {
      for (const el of els) {
        el.inert = false;
      }
    };
  }, [wurzel, verdecktSchluessel, zielElement]);

  // Gestapelt (Belegnotiz): Raster unter dem Bon um die Tastatur verlängern und Feld samt Summe über die
  // Tastatur scrollen – darunter liegt dann nur noch die Zahlleiste, nichts wird verdeckt.
  // Einmal je Ziel, damit eigenes Scrollen nicht zurückgedreht wird.
  // Höhe der Tastatur beobachten (Warnzeile kommt hinzu, Fenster ändert sich)
  useEffect(() => {
    const k = eigen.current;
    if (k === null || typeof ResizeObserver !== 'function') {
      return;
    }
    const b = new ResizeObserver(() => {
      setEigenHoehe(k.offsetHeight);
    });
    b.observe(k);
    return () => {
      b.disconnect();
    };
  }, []);
  const stapelUnten = lage?.stapelUnten === true;
  useLayoutEffect(() => {
    const kEl = eigen.current;
    if (!stapelUnten || kEl === null || zielElement === null) {
      return;
    }
    // Platz IM Raster (unter dem Bon, über der Zahlleiste): Die Zahlleiste klebt nur innerhalb
    // von .verkauf-raster unten – Platz am body ließe sie beim Scrollen ans Seitenende samt
    // Tastatur nach oben wandern und das Feld wieder verdecken.
    const bon = wurzel.current?.querySelector<HTMLElement>('.bon-panel') ?? null;
    if (bon === null) {
      return;
    }
    const alt = bon.style.marginBottom;
    bon.style.marginBottom = `${String(Math.ceil(kEl.offsetHeight + LUFT))}px`;
    const k = kEl.getBoundingClientRect();
    const summen = wurzel.current?.querySelector<HTMLElement>('.bon-summen');
    const unten = Math.max(
      zielElement.getBoundingClientRect().bottom,
      summen?.getBoundingClientRect().bottom ?? 0,
    );
    if (unten > k.top - LUFT) {
      window.scrollBy(0, unten - k.top + LUFT * 2);
    }
    return () => {
      bon.style.marginBottom = alt;
    };
    // Neu bei Wechsel ins gestapelte Layout, anderem Ziel, geänderter Tastatur- oder
    // Fensterhöhe – nicht bei jedem Scrollen, damit eigenes Scrollen nicht zurückgedreht wird
  }, [stapelUnten, zielElement, wurzel, eigenHoehe, fensterHoehe]);

  return (
    <div ref={eigen} className="bt-flaeche" style={lage?.stil ?? { visibility: 'hidden' }}>
      <Bildschirmtastatur steuerung={props.steuerung} warnzeile={props.warnzeile} />
    </div>
  );
}
